import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail, BRAND } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, MapPin, ArrowRight, ShieldCheck, KeyRound, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

function ForgotPinLink({ email, hospitalCode }) {
  const [loading, setLoading] = React.useState(false);
  const submit = async () => {
    if (!email) { toast.error("Couldn't read your email — sign out and try the link on the login page."); return; }
    setLoading(true);
    try {
      // The user just logged in, so we know hospital_code from session storage if we have it.
      const code = hospitalCode || localStorage.getItem("careo_last_hospital_code") || "";
      const { data } = await api.post("/auth/forgot-branch-pin", { email, hospital_code: code });
      if (data.reset_link) {
        toast.info(`SMTP not configured — admin can relay this link: ${window.location.origin}${data.reset_link}`, { duration: 15000 });
      } else {
        toast.success("Reset link sent to your registered email.");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setLoading(false); }
  };
  return (
    <button type="button" onClick={submit} disabled={loading} className="text-xs text-brand underline-offset-2 hover:underline disabled:opacity-50" data-testid="select-branch-forgot-pin">
      {loading ? "Sending link…" : "Forgot Branch PIN?"}
    </button>
  );
}

export default function SelectBranch() {
  const { pendingBranches, needsBranchPin, selectBranch, logout, user } = useAuth();
  const navigate = useNavigate();
  const [picked, setPicked] = useState(null);   // branch object the user clicked
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const signOut = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const branches = pendingBranches || [];

  if (!branches.length) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-secondary p-6">
        <Card className="max-w-md p-6 text-center">
          <div className="text-sm text-ink-muted">No branches are assigned to your account.</div>
          <Button variant="outline" onClick={signOut} className="mt-4" data-testid="select-branch-signout-fallback">Sign out</Button>
        </Card>
      </div>
    );
  }

  const enter = async (branch, providedPin) => {
    setError("");
    setLoading(true);
    try {
      await selectBranch(branch.id, providedPin || null);
      navigate("/app", { replace: true });
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || "Could not enter that branch.");
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (b) => {
    setError("");
    if (needsBranchPin) {
      setPicked(b);
      setPin("");
    } else {
      enter(b, null);
    }
  };

  const submitPin = (e) => {
    e?.preventDefault?.();
    if (!picked) return;
    if (!pin) { setError("Branch PIN is required."); return; }
    enter(picked, pin);
  };

  return (
    <div className="min-h-screen bg-secondary py-12">
      <div className="mx-auto max-w-3xl px-6">
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={BRAND.icon} alt="" className="h-9 w-9" />
            <div className="leading-tight">
              <div className="font-heading text-lg font-semibold text-brand">{BRAND.name}</div>
              <div className="text-[10px] text-ink-muted">{BRAND.tagline}</div>
              <div className="text-[11px] text-ink-muted mt-0.5">Signed in as <b>{user?.email}</b> · {user?.role?.replace(/_/g, " ")}</div>
            </div>
          </div>
          <button onClick={signOut} className="text-xs text-ink-muted hover:text-ink underline-offset-2 hover:underline" data-testid="select-branch-signout">Sign out</button>
        </div>

        {picked ? (
          <Card className="mx-auto max-w-md p-6">
            <button onClick={() => { setPicked(null); setError(""); }} className="mb-4 inline-flex items-center gap-1 text-xs text-ink-muted hover:text-ink" data-testid="select-branch-back">
              <ArrowLeft size={12} /> Back to branches
            </button>
            <div className="flex items-center gap-2 text-brand">
              <Building2 size={16} />
              <span className="text-xs font-semibold uppercase tracking-wider">{picked.code}</span>
            </div>
            <div className="mt-2 font-heading text-xl font-semibold">{picked.name}</div>
            {picked.address && <div className="mt-1 text-xs text-ink-muted">{picked.address}</div>}

            <form onSubmit={submitPin} className="mt-5 space-y-3">
              <div>
                <Label htmlFor="branch-pin" className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Branch PIN</Label>
                <div className="relative mt-1.5">
                  <KeyRound size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-subtle" />
                  <Input
                    id="branch-pin" data-testid="select-branch-pin-input"
                    type="password" inputMode="numeric" autoComplete="one-time-code" autoFocus
                    value={pin} onChange={(e) => setPin(e.target.value)}
                    placeholder="Enter PIN to open this branch" className="pl-9 tracking-widest"
                  />
                </div>
                <div className="mt-1 text-[11px] text-ink-muted">Set by your admin at the time of user creation.</div>
              </div>
              {error && <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive" data-testid="select-branch-error">{error}</div>}
              <Button type="submit" disabled={loading} className="w-full bg-brand hover:bg-brand-700" data-testid="select-branch-pin-submit">
                {loading ? "Entering…" : <>Enter {picked.name} <ArrowRight size={14} className="ml-2" /></>}
              </Button>
              <div className="text-center">
                <ForgotPinLink email={user?.email} hospitalCode={user?.hospital_code} />
              </div>
            </form>
          </Card>
        ) : (
          <>
            <h1 className="font-heading text-3xl font-semibold">Select branch</h1>
            <p className="mt-1 text-sm text-ink-muted">
              Choose the branch you want to work in. All records you create will be saved against this branch only.
              {needsBranchPin && " You'll be asked for your Branch PIN next."}
            </p>

            {error && !picked && <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive" data-testid="select-branch-error">{error}</div>}

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {branches.map((b) => (
                <Card key={b.id} className="flex flex-col justify-between p-5 transition hover:border-brand hover:shadow-md" data-testid={`select-branch-tile-${b.code || b.id}`}>
                  <div>
                    <div className="flex items-center gap-2 text-brand">
                      <Building2 size={16} />
                      <span className="text-xs font-semibold uppercase tracking-wider">{b.code}</span>
                    </div>
                    <div className="mt-2 font-heading text-lg font-semibold">{b.name}</div>
                    {b.address && (
                      <div className="mt-1 flex items-start gap-1 text-xs text-ink-muted">
                        <MapPin size={12} className="mt-0.5 shrink-0" />
                        <span>{b.address}</span>
                      </div>
                    )}
                  </div>
                  <Button
                    onClick={() => handleCardClick(b)}
                    disabled={loading}
                    className="mt-4 w-full bg-brand hover:bg-brand-700"
                    data-testid={`select-branch-login-${b.code || b.id}`}
                  >
                    {loading ? "Entering…" : <>Login to this branch <ArrowRight size={14} className="ml-2" /></>}
                  </Button>
                </Card>
              ))}
            </div>

            <div className="mt-8 inline-flex items-center gap-2 rounded-full border border-leaf/30 bg-leaf-50 px-3 py-1 text-[11px] font-medium text-leaf-700">
              <ShieldCheck size={12} /> Data shown after login is strictly limited to the selected branch.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
